"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatThresholdsSummary } from "@/components/optimizer/filters/drawer-controls";
import { CLEARED_EXTRA_QUERY } from "@/components/optimizer/filters/extra-query";
import { oddsRangeActive } from "@/components/optimizer/odds-range";
import type { OptimizerQueryState } from "@/components/optimizer/query-state";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import type { UserWeightPreset } from "@/lib/actions/weight-presets";
import { resolveWeights } from "@/lib/fantasy-props/rows";
import { useSavedQuickFilters } from "@/contexts/saved-quick-filters-context";
import {
  MAX_QUICK_FILTER_NAME_LENGTH,
  quickFilterActive,
  quickFilterPatch,
  quickFilterStateOf,
  sameQuickFilterName,
  type QuickFilterFields,
  type QuickFilterState,
} from "@/lib/saved-quick-filters";
import { chipClass, SectionLabel } from "./chrome";

type QuickFilter = "sharp" | "longshots" | "favorites";

const SAVED_PRESET_ID = "saved";

const QUICK_FILTERS: readonly { readonly id: QuickFilter; readonly label: string; readonly meta: string; readonly update: Partial<OptimizerQueryState> }[] = [
  { id: "sharp", label: "Sharp", meta: "3 books", update: { ...CLEARED_EXTRA_QUERY, minDataPoints: 3 } },
  { id: "longshots", label: "Longshots", meta: "<35%", update: { ...CLEARED_EXTRA_QUERY, maxOddsToHit: 35 } },
  { id: "favorites", label: "Favorites", meta: ">60%", update: { ...CLEARED_EXTRA_QUERY, minOddsToHit: 60 } },
];

export function activeQuickFilter(state: OptimizerQueryState): QuickFilter | "" {
  if (state.requiredComparisonBooks.length > 0 || state.weights !== null || state.requiredBooks.length > 0 || state.statTypes.length > 0 || state.matchups.length > 0 || state.overUnder !== "" || oddsRangeActive(state)) return "";
  const dp = state.minDataPoints ?? 0;
  const ev = state.minEV ?? 0;
  const minH = state.minOddsToHit ?? 0;
  const maxH = state.maxOddsToHit ?? 100;
  if (ev !== 0) return "";
  if (dp === 3 && minH === 0 && maxH === 100) return "sharp";
  if (dp === 0 && minH === 0 && maxH === 35) return "longshots";
  if (dp === 0 && minH === 60 && maxH === 100) return "favorites";
  return "";
}

function savedSummary(patch: Partial<QuickFilterState>, fields: QuickFilterFields): string {
  const parts = [formatThresholdsSummary({
    minDataPoints: patch.minDataPoints ?? 0,
    minLiquidity: patch.minLiquidity ?? 0,
    minEV: patch.minEV ?? 0,
    showMinEV: fields.minEV,
    minOddsToHit: patch.minOddsToHit ?? 0,
    maxOddsToHit: patch.maxOddsToHit ?? 100,
    minOdds: patch.minOdds ?? null,
    maxOdds: patch.maxOdds ?? null,
    requiredBookCount: patch.requiredComparisonBooks?.length ?? 0,
  })];
  const weighted = patch.weights ? Object.keys(patch.weights).length : 0;
  if (patch.preset) parts.push(patch.preset);
  else if (weighted > 0) parts.push(`${weighted} ${weighted === 1 ? "book" : "books"} weighted`);
  if (patch.requiredBooks && patch.requiredBooks.length > 0) parts.push(`${patch.requiredBooks.length} required`);
  return parts.join(" · ");
}

function CloseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
  );
}

export function QuickFilters({
  form, fields, ownBook, userPresets, tall, onPick,
}: {
  form: OptimizerQueryState;
  fields: QuickFilterFields;
  ownBook: PlatformType | null;
  userPresets: readonly UserWeightPreset[];
  tall: boolean;
  onPick: (update: Partial<OptimizerQueryState>, presetId: string) => void;
}) {
  const saved = useSavedQuickFilters();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const quick = activeQuickFilter(form);
  const replacing = saved.filters.some((filter) => sameQuickFilterName(filter.name, name));
  const userPresetNames = userPresets.map((preset) => preset.name);
  const shown = { ...form, weights: resolveWeights(form, userPresets) };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving || name.trim().length === 0) return;
    setSaving(true);
    try {
      if (!(await saved.save(name, quickFilterStateOf(shown, fields), fields))) return;
      setNaming(false);
      setName("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-label="Quick filters">
      <div className="flex items-center justify-between pr-1.5">
        <SectionLabel>Quick filters</SectionLabel>
        {saved.available && (
          <div className="flex pt-2 text-[11px] font-medium">
            <button type="button" aria-expanded={naming} onClick={() => setNaming((current) => !current)} className={cn("rounded-md text-zinc-400 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", tall && "h-11 px-2")}>
              {naming ? "Cancel" : "Save current"}
            </button>
          </div>
        )}
      </div>
      {naming && (
        <form onSubmit={submit} className="flex items-center gap-1.5 px-1.5 pb-1.5">
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={MAX_QUICK_FILTER_NAME_LENGTH}
            placeholder="Filter name"
            aria-label="Saved filter name"
            className={cn("min-w-0 flex-1 rounded-md bg-white/[0.04] px-2.5 text-foreground placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary/50", tall ? "h-9 text-[16px]" : "h-8 text-[13px]")}
          />
          <button type="submit" disabled={saving || name.trim().length === 0} className={cn(chipClass(true, tall), "px-3 disabled:cursor-not-allowed disabled:opacity-40")}>
            {replacing ? "Replace" : "Save"}
          </button>
        </form>
      )}
      <div className="grid grid-cols-2 gap-1.5 px-1.5 pb-2">
        {QUICK_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            aria-pressed={quick === filter.id}
            onClick={() => onPick(filter.update, filter.id)}
            className={cn(chipClass(quick === filter.id, tall), "justify-between")}
          >
            <span>{filter.label}</span>
            <span className={cn("data-num text-[11px]", quick === filter.id ? "text-primary/80" : "text-zinc-500")}>{filter.meta}</span>
          </button>
        ))}
        {saved.filters.map((filter) => {
          const patch = quickFilterPatch(filter.state, fields, userPresetNames);
          const active = quickFilterActive(shown, patch, ownBook);
          return (
            <div key={filter.id} className={cn(chipClass(active, tall), "min-w-0 justify-between gap-0 px-0")}>
              <button
                type="button"
                aria-pressed={active}
                title={savedSummary(patch, fields)}
                onClick={() => onPick(patch, SAVED_PRESET_ID)}
                className="h-full min-w-0 flex-1 truncate rounded-l-md pl-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
              >
                {filter.name}
              </button>
              <button
                type="button"
                aria-label={`Delete ${filter.name}`}
                onClick={() => { saved.remove(filter.id).catch(() => {}); }}
                className={cn(
                  "flex h-full flex-shrink-0 items-center justify-center rounded-r-md hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
                  tall ? "w-9" : "w-7",
                  active ? "text-primary/70" : "text-zinc-500",
                )}
              >
                <CloseIcon />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
